import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { Select } from "@mantine/core";
import { IconCar } from "@tabler/icons-react";
import type { Vehicle } from "@models/repairInformation";
import { vehicleId, vehicleLabel } from "./vehicles";

interface VehiclePickerProps {
  vehicles: Vehicle[];
  value: Vehicle | null;
  onChange: (vehicle: Vehicle | null) => void;
  loading?: boolean;
  error?: string | null;
}

/** The searchable vehicle list. The user always picks a vehicle before asking. */
const VehiclePicker: React.FC<VehiclePickerProps> = ({ vehicles, value, onChange, loading, error }) => {
  const byId = useMemo(() => new Map(vehicles.map((v) => [vehicleId(v), v])), [vehicles]);
  const data = useMemo(
    () => vehicles.map((v) => ({ value: vehicleId(v), label: vehicleLabel(v) })),
    [vehicles],
  );
  const [search, setSearch] = useState("");
  useEffect(() => setSearch(value ? vehicleLabel(value) : ""), [value]);

  return (
    <Select
      label="Vehicle"
      withAsterisk
      size="xs"
      placeholder={loading ? "Loading vehicles…" : "Search make, model or type"}
      leftSection={<IconCar size={14} />}
      data={data}
      value={value ? vehicleId(value) : null}
      onChange={(id) => onChange(id ? byId.get(id) ?? null : null)}
      searchable
      searchValue={search}
      onSearchChange={setSearch}
      nothingFoundMessage={vehicles.length === 0 ? "No vehicles published yet" : "No vehicle matches"}
      limit={50}
      clearable
      disabled={loading}
      error={error ?? undefined}
      comboboxProps={{ withinPortal: true }}
      w="100%"
    />
  );
};

export default VehiclePicker;
